import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {UserIcon} from "@heroicons/react/outline";
import {PlusIcon, UserIcon as UserIconSolid} from "@heroicons/react/solid";
import {formattedDateTime} from "services/date";
import {useNavigate} from "react-router-dom";
import SearchBar from "components/SearchBar";
import Button from "components/Button";
import useSorting from "../../../hooks/useSorting";
import {SortDirection} from "../../../types/SortDirection";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import ContactContextMenu from "components/menus/ContactContextMenu";
import {Contact} from "../../../types/Contact";
import {DeleteContact} from "../index";
import AudienceMemberContextMenu from "../../../components/menus/AudienceMemberContextMenu";

function ContactList() {
  const navigate = useNavigate();
  const auth = useAuth();

  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const contacts = useQuery(
    ['contacts.all', {search, sort, page}],
    () => ngage.contacts.filtered({search, sort, page}),
    {keepPreviousData: true}
  )

  const [deletingContact, setDeletingContact] = useState<Contact | null>(null);

  // Prefetch next and previous pages
  prefetch('contacts', {sort, page, search}, ngage.contacts.filtered);

  // Prepare meta data
  const meta = [
    {icon: UserIconSolid, value: (contacts.data?.meta.total ?? 0).toLocaleString() + ' total contacts'}
  ]

  const breadcrumbs = {'Engagement': '/', 'Contacts': '/contacts'}
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/contacts/new')}>New</Button>

  // Prevent unauthorised viewing
  if (auth.cannot('contacts:read')) {
    return <NotAuthorised/>
  }

  return (
    <Page
      title="Contacts"
      subTitle='Contacts associated with users'
      meta={meta}
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={contacts.isFetching} onChange={setSearch}/>
      </div>

      <Table paginationData={contacts.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.first_name ?? SortDirection.NONE}
              onSort={direction => sortBy('first_name', direction)}>
              Name
            </TableHeader>

            <TableHeader>Owner name</TableHeader>

            <TableHeader>Owner email address</TableHeader>

            <TableHeader
              sortable
              sortDirection={currentSort.created_at ?? SortDirection.NONE}
              onSort={direction => sortBy('created_at', direction)}>
              Created
            </TableHeader>

            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={contacts.isLoading}>

          {contacts?.data && contacts.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={8}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <UserIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No contacts</span>
                </div>
              </TableData>
            </TableRow>
            : contacts?.data?.data.map((contact, index) => (
              <TableRow key={contact.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <ContactContextMenu
                        contact={contact}
                        onDelete={() => setDeletingContact(contact)}
                        position='left'
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <UserIcon className="h-5 w-5 mr-1 text-gray-500"/> {contact.full_name}
                    </div>
                  </div>
                </TableData>
                <TableData>{contact.owner?.name}</TableData>
                <TableData>{contact.owner?.email}</TableData>
                <TableData>{formattedDateTime(contact.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <ContactContextMenu
                    contact={contact}
                    onDelete={() => setDeletingContact(contact)}
                  />
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteContact
                  contact={deletingContact}
                  show={contact === deletingContact}
                  onCancel={() => setDeletingContact(null)}
                  onDelete={() => {
                    setDeletingContact(null);
                    contacts?.refetch();
                  }}
                  onDeleteFail={() => setDeletingContact(null)}
                />
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Page>
  );
}

export default ContactList;